import React, { useState } from 'react';
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  FormControlLabel,
  Switch,
  InputAdornment,
  Alert
} from '@mui/material';
import Grid from '@mui/material/Grid';
import SaveIcon from '@mui/icons-material/Save';
import SecurityIcon from '@mui/icons-material/Security';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { useTranslation } from 'react-i18next';

interface SecuritySettingsProps {
  security: any;
  onUpdate: (security: any) => Promise<void>;
}

export default function SecuritySettings({ security, onUpdate }: SecuritySettingsProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    session_timeout_minutes: security.session_timeout_minutes || 480,
    max_login_attempts: security.max_login_attempts || 5,
    lockout_duration_minutes: security.lockout_duration_minutes || 15,
    password_min_length: security.password_min_length || 8,
    password_require_uppercase: security.password_require_uppercase ?? true,
    password_require_lowercase: security.password_require_lowercase ?? true,
    password_require_numbers: security.password_require_numbers ?? true,
    password_require_special: security.password_require_special ?? true,
    password_history_count: security.password_history_count || 5,
    two_factor_enabled: security.two_factor_enabled ?? false,
    allowed_ip_ranges: security.allowed_ip_ranges || [],
    enable_audit_log: security.enable_audit_log ?? true,
    log_retention_days: security.log_retention_days || 90,
    enable_rate_limiting: security.enable_rate_limiting ?? true,
    api_rate_limit_per_minute: security.api_rate_limit_per_minute || 100,
    jwt_expiration_hours: security.jwt_expiration_hours || 8
  });
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const handleChange = (field: string, value: any) => {
    setFormData({
      ...formData,
      [field]: value
    });
    setHasChanges(true);
  };

  const handleIpRangesChange = (value: string) => {
    const ranges = value.split(',').map(range => range.trim()).filter(range => range);
    handleChange('allowed_ip_ranges', ranges);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onUpdate(formData);
      setHasChanges(false);
    } finally {
      setIsSaving(false);
    }
  };

  const getPasswordStrength = () => {
    let score = 0;
    if (formData.password_min_length >= 8) {score++;}
    if (formData.password_require_uppercase) {score++;}
    if (formData.password_require_lowercase) {score++;}
    if (formData.password_require_numbers) {score++;}
    if (formData.password_require_special) {score++;}
    
    if (score <= 2) {return { level: 'weak', color: 'error' as const };}
    if (score <= 4) {return { level: 'medium', color: 'warning' as const };}
    return { level: 'strong', color: 'success' as const };
  };

  const passwordStrength = getPasswordStrength();

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('system.security.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {t('system.security.description')}
        </Typography>

        <Grid container spacing={3}>
          <Grid size={12}>
            <Typography variant="subtitle1" gutterBottom>
              {t('system.security.authentication')}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('system.security.sessionTimeout')}
              type="number"
              value={formData.session_timeout_minutes}
              onChange={(e) => handleChange('session_timeout_minutes', parseInt(e.target.value) || 0)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <VpnKeyIcon />
                  </InputAdornment>
                ),
                endAdornment: <InputAdornment position="end">{t('common.minutes')}</InputAdornment>,
              }}
              helperText={t('system.security.sessionTimeoutHelp')}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('system.security.jwtExpiration')}
              type="number"
              value={formData.jwt_expiration_hours}
              onChange={(e) => handleChange('jwt_expiration_hours', parseInt(e.target.value) || 0)}
              InputProps={{
                endAdornment: <InputAdornment position="end">{t('common.hours')}</InputAdornment>,
              }}
              helperText={t('system.security.jwtExpirationHelp')}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('system.security.maxLoginAttempts')}
              type="number"
              value={formData.max_login_attempts}
              onChange={(e) => handleChange('max_login_attempts', parseInt(e.target.value) || 0)}
              helperText={t('system.security.maxLoginAttemptsHelp')}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('system.security.lockoutDuration')}
              type="number"
              value={formData.lockout_duration_minutes}
              onChange={(e) => handleChange('lockout_duration_minutes', parseInt(e.target.value) || 0)}
              InputProps={{
                endAdornment: <InputAdornment position="end">{t('common.minutes')}</InputAdornment>,
              }}
              helperText={t('system.security.lockoutDurationHelp')}
            />
          </Grid>

          <Grid size={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              {t('system.security.passwordPolicy')}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('system.security.passwordMinLength')}
              type="number"
              value={formData.password_min_length}
              onChange={(e) => handleChange('password_min_length', parseInt(e.target.value) || 0)}
              inputProps={{ min: 1, max: 128 }}
              helperText={t('system.security.passwordMinLengthHelp')}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('system.security.passwordHistory')}
              type="number"
              value={formData.password_history_count}
              onChange={(e) => handleChange('password_history_count', parseInt(e.target.value) || 0)}
              helperText={t('system.security.passwordHistoryHelp')}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.password_require_uppercase}
                  onChange={(e) => handleChange('password_require_uppercase', e.target.checked)}
                />
              }
              label={t('system.security.requireUppercase')}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.password_require_lowercase}
                  onChange={(e) => handleChange('password_require_lowercase', e.target.checked)}
                />
              }
              label={t('system.security.requireLowercase')}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.password_require_numbers}
                  onChange={(e) => handleChange('password_require_numbers', e.target.checked)}
                />
              }
              label={t('system.security.requireNumbers')}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.password_require_special}
                  onChange={(e) => handleChange('password_require_special', e.target.checked)}
                />
              }
              label={t('system.security.requireSpecial')}
            />
          </Grid>

          <Grid size={12}>
            <Alert severity={passwordStrength.color} sx={{ mt: 1 }}>
              {t(`system.security.passwordStrength.${passwordStrength.level}`)}
            </Alert>
          </Grid>

          <Grid size={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              {t('system.security.accessControl')}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.two_factor_enabled}
                  onChange={(e) => handleChange('two_factor_enabled', e.target.checked)}
                />
              }
              label={t('system.security.twoFactorAuth')}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.enable_rate_limiting}
                  onChange={(e) => handleChange('enable_rate_limiting', e.target.checked)}
                />
              }
              label={t('system.security.enableRateLimit')}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('system.security.apiRateLimit')}
              type="number"
              value={formData.api_rate_limit_per_minute}
              onChange={(e) => handleChange('api_rate_limit_per_minute', parseInt(e.target.value) || 0)}
              disabled={!formData.enable_rate_limiting}
              InputProps={{
                endAdornment: <InputAdornment position="end">/min</InputAdornment>,
              }}
              helperText={t('system.security.apiRateLimitHelp')}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              label={t('system.security.allowedIpRanges')}
              value={formData.allowed_ip_ranges.join(', ')}
              onChange={(e) => handleIpRangesChange(e.target.value)}
              placeholder="192.168.1.0/24, 10.0.0.0/8"
              helperText={t('system.security.allowedIpRangesHelp')}
              multiline
              rows={2}
            />
          </Grid>

          <Grid size={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              {t('system.security.auditLogging')}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.enable_audit_log}
                  onChange={(e) => handleChange('enable_audit_log', e.target.checked)}
                />
              }
              label={t('system.security.enableAuditLog')}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('system.security.logRetention')}
              type="number"
              value={formData.log_retention_days}
              onChange={(e) => handleChange('log_retention_days', parseInt(e.target.value) || 0)}
              disabled={!formData.enable_audit_log}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SecurityIcon />
                  </InputAdornment>
                ),
                endAdornment: <InputAdornment position="end">{t('common.days')}</InputAdornment>,
              }}
              helperText={t('system.security.logRetentionHelp')}
            />
          </Grid>

          <Grid size={12}>
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('system.security.securityWarning')}
            </Alert>
          </Grid>
        </Grid>

        <Box mt={3} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
          >
            {t('common.save')}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}